/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Observable, of, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

// Models
import { ApiResponse, GcpUploadResponse } from '@core/models';
import { USER_TYPES } from '@core/models/mongo';

// Services
import { ApiService } from 'app/services/api.service';
import { MongoUser } from '@core/models/mongo';

@Injectable({
  providedIn: 'root',
})
export class SuperAdminUserService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  searchSuperAdminUsers(): Observable<ApiResponse> {
    const searchParams = {
      tenant_id: 'ZUUM',
      userTypes: [USER_TYPES.SUPER_ADMIN_MANAGER],
      isDeleted: false,
    };
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/search`, searchParams)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  getSuperAdmin(userId: string): Observable<ApiResponse<MongoUser>> {
    return this.http
      .get<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/${userId}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  createSuperAdminUser(user: MongoUser): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/user?sendInvite=true`, user)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateSuperAdminUser(user: Partial<MongoUser>): Observable<ApiResponse> {
    const { _id: removed, ...updatedUser } = user;
    return this.http
      .put<ApiResponse>(`${this.apiService.coreApi}/users/${user._id}`, updatedUser)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  deleteSuperAdminUser(id: string | undefined): Observable<any> {
    if (!id) {
      return of(null);
    }
    return this.http
      .delete<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/${id}`)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  updateProfilePicture(image: File): Observable<ApiResponse<GcpUploadResponse>> {
    const formData: FormData = new FormData();
    formData.append('file', image);
    return this.http
      .post<ApiResponse<GcpUploadResponse>>(`${this.apiService.storageApi}/upload-file`, formData)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  inviteSuperAdmin(email: string, tenantId: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/invite-super-admin`, {
        email,
        tenantId,
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  checkPhoneNumber(params): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/check-phoneNumber`, params);
  }

  checkEmail(params): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/check-email`, params);
  }

  checkBrokerEmail(params: { email: string; type: string }): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiService.superAdminApi}/users/check`, params);
  }

  // Password Reset
  sendForgotPasswordLink(email: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/forgot-password/generate-token/${email}`, {
        platform: 'super-admin',
      })
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  checkForgotPasswordToken(token: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/forgot-password/check-token`, token)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  confirmPasswordReset(params: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/forgot-password/change-password`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  // complete user registration
  checkUserRegistrationToken(params: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/confirm-user/check-token`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }

  confirmUserRegistration(params: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.coreApiSuperAdmin}/users/confirm-user/submit`, params)
      .pipe(catchError((error: HttpErrorResponse) => observableThrowError(error)));
  }
}
